import React from "react";
import styled from "styled-components";

import { Button, HSpace, VSpace } from "@examspark/common-ui";
import {
  IconInformationCircleLine,
  IconRefreshLine,
} from "@examspark/common-ui/dist/components/icons";
import {
  ExamBlobs,
  ExamConfigData,
  ExamItemResponses,
  ExamScore,
  ExamSessionMetadata,
} from "@examspark/types";

import ResultItem from "./ResultItem";

const Styled = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 0 0;
`;
const Center = styled.div`
  margin: 0 auto;
  max-width: 60rem;
`;
const Headline = styled.h1`
  font-size: 3em;
  margin: 0;
  font-weight: 400;
  margin-top: 2rem;
`;
const Subheadline = styled.h2`
  font-size: 1.5em;
  color: #666;
  margin: 0;
  font-weight: normal;

  span {
    color: green;
  }
`;

const Unscored = styled(HSpace)`
  background-color: #f2f2f2;
  padding: 1rem;
  color: #333;
  align-items: center;
  border: solid 1px #ddd;
  border-radius: 0.5rem;

  svg {
    fill: #666;
    display: block;
  }
`;

const ItemList = styled.div``;

const Results = (props: {
  config?: ExamConfigData;
  sessionData?: ExamSessionMetadata;
  itemPreviewer?: any;
  responses?: ExamItemResponses;
  blobs?: ExamBlobs;
  results: ExamScore;
  onRequestRetake?: () => void;

  // extension point for adding additional Results controls.
  // Presently used by Exam Attempts to show the attempt picker.
  toolbar?: React.ReactNode;
}) => {
  const scoreMax = props.results.items.reduce((state, item) => {
    if (item.score.actual != null) state = state + item.score.max;
    return state;
  }, 0);
  const scoreActual = props.results.items.reduce((state, item) => {
    if (item.score.actual != null) state = state + item.score.actual;
    return state;
  }, 0);

  const score = Math.round(((scoreActual ?? 0) / scoreMax) * 100);
  let headline = "";

  if (score >= 100) {
    headline = "Sheer Perfection! 💯";
  } else if (score >= 90) {
    headline = "Nicely Done! 🤓";
  } else if (score >= 80) {
    headline = "Good Work! 🙂";
  } else if (score >= 70) {
    headline = "You're On the Right Track! 🚃";
  } else {
    headline = "There's Room to Improve! 💪";
  }

  const hasUnscoredItems = props.results.items.some(
    (item) => item.score.actual == null,
  );

  return (
    <Styled>
      <Center>
        <VSpace size="3rem">
          <VSpace>
            <Headline>{headline}</Headline>
            <Subheadline>
              You scored <span>{score || "0"}%</span>.
            </Subheadline>
            {hasUnscoredItems && (
              <Unscored>
                <div>
                  <IconInformationCircleLine width="16" />
                </div>
                <div>
                  This score does not account for questions that still require
                  manual scoring from an instructor.
                </div>
              </Unscored>
            )}
            {props.toolbar != null ? props.toolbar : null}
          </VSpace>
          {props.config?.allowRetakes && (
            <div>
              <Button
                title="Retake Activity"
                variant="outlined"
                icon={<IconRefreshLine />}
                onClick={props.onRequestRetake}
              >
                Retake Actvivity
              </Button>
            </div>
          )}
          <ItemList>
            <VSpace>
              {props.results.items.map((item, idx: number) => {
                return (
                  <ResultItem
                    key={idx}
                    idx={idx}
                    item={item}
                    config={props.config}
                    sessionData={props.sessionData}
                    itemPreviewer={props.itemPreviewer}
                    response={props.responses?.[item.id]}
                    blobs={props.blobs?.[item.id]}
                  />
                );
              })}
            </VSpace>
          </ItemList>
        </VSpace>
      </Center>
    </Styled>
  );
};

export default Results;
